<template>
    <div class="email-body">
        <div v-if="row.attachmentURL" class="audio-attachment">
            <audio
                class="mb-3"
                controls
                v-if="isAudio(row.attachmentURL)"
                :src="row.attachmentURL"
            ></audio>
        </div>

        <div v-if="attachments.length" class="email-attachments">
            <div class="email-attachments-label">
                <v-icon small class="mr-1">mdi-paperclip</v-icon>
                {{ attachments.length }}
                {{ attachments.length === 1 ? "attachment" : "attachments" }}
                <v-btn
                    v-if="showManageFiles && hasManageableFiles"
                    small
                    color="success"
                    class="ml-3"
                    @click.stop="openManageFiles"
                >
                    Manage files
                </v-btn>
            </div>
            <div class="email-attachment-list">
                <v-chip
                    v-for="file in attachments"
                    :key="file.id"
                    class="ma-1 email-attachment-chip"
                    outline
                    color="primary"
                    @click="openFile(file)"
                >
                    <v-icon left small>{{ fileIcon(file) }}</v-icon>
                    {{ file.fileName }}
                    <v-icon right small>
                        {{ canPreview(file) ? "mdi-eye" : "mdi-download" }}
                    </v-icon>
                </v-chip>
            </div>
        </div>

        <div v-if="showBody && hasHtml" class="email-html-wrap">
            <iframe
                class="email-html-frame"
                sandbox="allow-same-origin allow-popups"
                :srcdoc="row.bodyHtml"
            ></iframe>
        </div>
        <div v-else-if="showBody && row.text" class="email-plain-text">
            {{ row.text }}
        </div>

        <document-preview-dialog
            v-model="preview.open"
            :url="preview.url"
            :name="preview.name"
            :kind="preview.kind"
        ></document-preview-dialog>
    </div>
</template>
<script>
export default {
    props: {
        row: Object,
        isIncoming: {
            type: Boolean,
            default: false,
        },
        columns: Array,
        reloadGrid: Function,
        showBody: {
            type: Boolean,
            default: true,
        },
        showManageFiles: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            attachments: [],
            preview: {
                open: false,
                url: "",
                name: "",
                kind: "",
            },
        };
    },
    watch: {
        rowId: function() {
            this.attachments = [];
            this.loadAttachments();
        },
    },
    computed: {
        rowId() {
            return this.row && this.row.id;
        },
        hasHtml() {
            const html = this.row && this.row.bodyHtml;
            return !!(html && html.trim() && /<[a-z][\s\S]*>/i.test(html));
        },
        hasManageableFiles() {
            return this.attachments.some((file) => this.isManageable(file));
        },
    },
    async created() {
        await this.loadAttachments();
    },
    methods: {
        isAudio(url) {
            return url && !!url.match(/\.(mp3|wav|wma)$/i);
        },
        async loadAttachments() {
            if (!this.row || !this.row.id || !this.row.hasAttachments) {
                return;
            }

            try {
                const files = await this.$http.get(
                    "api/emails/getAttachments?emailID=" + this.row.id
                );
                this.attachments = files || [];
            } catch (e) {
                this.attachments = [];
            }
        },
        fileKind(file) {
            const name = ((file && file.fileName) || "").toLowerCase();
            const type = ((file && file.contentType) || "").toLowerCase();
            const ext = name.includes(".") ? name.split(".").pop() : "";

            if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
                return "image";
            }
            if (type === "application/pdf" || ext === "pdf") {
                return "pdf";
            }
            if (type.startsWith("audio/") || ["mp3", "wav", "wma", "ogg", "m4a"].includes(ext)) {
                return "audio";
            }
            if (type.startsWith("video/") || ["mp4", "webm", "mov"].includes(ext)) {
                return "video";
            }
            if (["txt", "html", "htm"].includes(ext) || type.startsWith("text/")) {
                return "other";
            }

            return "download";
        },
        canPreview(file) {
            return this.fileKind(file) !== "download";
        },
        isManageable(file) {
            const kind = this.fileKind(file);
            if (kind !== "pdf" && kind !== "image" || !this.isIncoming) {
                return false;
            }

            const name = ((file && file.fileName) || "").toLowerCase();
            const ext = name.includes(".") ? name.split(".").pop() : "";
            return ext !== "svg" && ext !== "webp";
        },
        openManageFiles() {
            if (!this.row || !this.row.id) {
                return;
            }

            this.$router.push({
                name: "DocumentSplitter",
                params: {
                    source: "email",
                    sourceId: String(this.row.id),
                },
            });
        },
        fileIcon(file) {
            const kind = this.fileKind(file);
            if (kind === "image") return "mdi-file-image";
            if (kind === "pdf") return "mdi-file-pdf";
            if (kind === "audio") return "mdi-file-music";
            if (kind === "video") return "mdi-file-video";
            return "mdi-file";
        },
        openFile(file) {
            const kind = this.fileKind(file);
            const url = file.fileURL;

            if (!url) {
                return;
            }

            if (kind === "download") {
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.rel = "noopener";
                link.download = file.fileName || "attachment";
                link.click();
                return;
            }

            this.preview = {
                open: true,
                url,
                name: file.fileName || "Attachment",
                kind,
            };
        },
    },
};
</script>
<style>
.email-body {
    max-width: 1000px;
    padding: 0 4px 8px 16px;
}

.email-attachments {
    margin-bottom: 12px;
}

.email-attachments-label {
    color: #607d8b;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
}

.email-attachment-chip {
    cursor: pointer;
}

.email-html-wrap {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
}

.email-html-frame {
    width: 100%;
    min-height: 220px;
    height: 480px;
    border: 0;
    background: #fff;
}

.email-plain-text {
    white-space: pre-line;
    line-height: 1.45;
}

.email-preview-body {
    background: #111;
    min-height: 200px;
}

.email-preview-image,
.email-preview-video {
    display: block;
    max-width: 100%;
    max-height: 80vh;
    margin: 0 auto;
}

.email-preview-frame {
    width: 100%;
    height: 75vh;
    border: 0;
    background: #fff;
}
</style>
